/**
 * CREATIVE DETONATOR - GENERATOR ORCHESTRATOR
 * Main entry point for the generator page
 *
 * This file coordinates all generator modules and handles:
 * - DOM element caching
 * - Module initialization
 * - Phase registration
 * - Backend status checking
 * - URL parameter handling
 *
 * ARCHITECTURE:
 * This is a slim orchestrator that imports from ES6 modules in ./generator/
 * All business logic has been extracted to specialized modules.
 */

// ============================================================================
// MODULE IMPORTS
// ============================================================================

// Phase 2: Import Store FIRST to ensure it's available for state management
// CRITICAL: Must be imported before any module that uses GeneratorState
import '../../js/core/store.js';

// Cache-busting version: v79 - Added Store import for Phase 2 state management
// Auth (unified auth initialization)
import { authState } from '../auth/js/state.js';
import { initGeneratorAuth } from '../auth/js/auth-init.js';
import { setUser } from './core/state.js';

// Core
import { API_CONFIG, GeneratorState, setElements, setSession, setBrand, clearBrand, getBrandId, hasBrand, restoreSessionState, clearSessionState } from './core/state.js';
import { updateBrief } from '../../js/store/actions/generator.js';
import { registerPhaseInit, goToPhase } from './core/navigation.js';
import { addTrigger, setTriggers, setFormats } from '../../js/store/actions/generator.js';

// API client for fetching full session data on reopen
import { api } from '../../js/api/client.js';

// Phases
import { initPhase1 } from './phases/phase1-ignition.js';
import { initPhase2, updateCreditCounter } from './phases/phase2-arming.js';
import { runDetonation } from './phases/phase3-detonation.js';
import { initPhase4 } from './phases/phase4-harvest.js';

// UI
import { initClickablePhases } from './ui/dev-mode.js';

// These modules self-register to window for compatibility
import './core/utils.js';
import './features/export.js';
import './ui/copy-dropdown.js';

// Video Generation
import { initVideoGeneration } from './features/video-generation.js';

// Version History
import { initVersionHistory } from './features/version-history.js';

// Shared modules
import { Toast } from '../../js/shared/toast.js';
import { Haptics } from '../../js/shared/haptics.js';

// ============================================================================
// DOM ELEMENT CACHE
// ============================================================================

/**
 * Cache DOM elements for performance
 * Called once during initialization
 * @returns {Object} Object containing all cached DOM elements
 */
function cacheElements() {
  return {
    // Phase indicators
    phaseSteps: document.querySelectorAll('.phase-step'),
    phaseDots: document.querySelectorAll('.phase-dot'),
    phaseLines: document.querySelectorAll('.phase-line'),
    phases: document.querySelectorAll('.generator-phase'),

    // Phase 1
    questionScreens: document.querySelectorAll('.question-screen'),
    devNavBtns: document.querySelectorAll('.dev-nav-btn'),
    navProductValue: document.getElementById('nav-product-value'),
    navBenefitValue: document.getElementById('nav-benefit-value'),
    navFormatValue: document.getElementById('nav-format-value'),
    inputProduct: document.getElementById('input-product'),
    inputBenefit: document.getElementById('input-benefit'),
    formatCards: document.querySelectorAll('.format-card'),
    btnToArming: document.getElementById('btn-to-arming'),

    // Phase 2
    armingBriefText: document.getElementById('arming-brief-text'),
    triggerGrid: document.getElementById('trigger-selection-grid'),
    selectionCount: document.getElementById('selection-count'),
    ideaPreviewText: document.getElementById('idea-preview-text'),
    btnDetonate: document.getElementById('btn-detonate'),
    categoryTabs: document.querySelectorAll('.category-tab'),

    // Phase 3 - Detonation UI handles its own DOM via detonation-ui.js
    // (No longer caching progressBar, currentTrigger, detonationStatus)

    // Phase 4
    harvestResults: document.getElementById('harvest-results'),
    harvestBriefText: document.getElementById('harvest-brief-text'),
    ideaCount: document.getElementById('idea-count'),
    btnExportTxt: document.getElementById('btn-export-txt'),
    btnExportPdf: document.getElementById('btn-export-pdf')
    // Note: SELECT ALL / CLEAR buttons are now in ActionBar (see phase4-harvest.js)
  };
}

// ============================================================================
// BACKEND STATUS CHECK
// ============================================================================

/**
 * Check backend connectivity
 * Logs status to console, warns if unavailable
 */
async function checkBackendStatus() {
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.status}`);
    const data = await response.json();
    console.log('Backend connected:', data);
  } catch (error) {
    console.warn('Backend not available. Start with: cd backend && npm start');
  }
}

// ============================================================================
// URL PARAMETER HANDLING
// ============================================================================

/**
 * Check for pre-selected trigger from URL
 * Supports ?trigger=A1 format
 */
function checkPreselectedTrigger() {
  const urlParams = new URLSearchParams(window.location.search);
  const triggerCode = urlParams.get('trigger');

  if (triggerCode) {
    addTrigger(triggerCode.toUpperCase());
  }
}

/**
 * Check for session reopen from Dashboard
 * Supports ?reopen=<sessionId> format
 * Fetches full session data from API for export functionality
 * Falls back to localStorage if API fails
 *
 * @returns {Promise<boolean>} True if reopening a session
 */
async function checkReopenSession() {
  const urlParams = new URLSearchParams(window.location.search);
  const reopenId = urlParams.get('reopen');

  if (!reopenId) return false;

  console.log('[Generator] Reopening session:', reopenId);

  // Try to fetch full session data from API first (needed for export)
  let fullSessionData = null;
  try {
    console.log('[Generator] Fetching session from API...');
    const result = await api.getSession(reopenId);
    // api.getSession returns {success: true, session: {...}} on success
    console.log('[Generator] API response:', result.success ? 'success' : 'failed');

    if (result.success && result.session) {
      fullSessionData = result.session;
      console.log('[Generator] Fetched full session from API');

      // Check if we have phase data for export
      const meta = fullSessionData.meta || {};
      const hasPhaseData = !!(meta.phase1 || meta.phase2a || meta.phase2b);
      console.log('[Generator] Session has phase data for export:', hasPhaseData);
      console.log('[Generator] Meta keys:', Object.keys(meta));
    }
  } catch (e) {
    console.warn('[Generator] Could not fetch session from API, using localStorage fallback:', e.message);
  }

  // Read session data from localStorage as fallback (DashboardCore uses 'cd_' prefix)
  const sessionDataRaw = localStorage.getItem('cd_reopen_session');
  let localSessionData = null;

  if (sessionDataRaw) {
    try {
      localSessionData = JSON.parse(sessionDataRaw);
    } catch (e) {
      console.error('[Generator] Failed to parse localStorage session data:', e);
    }
  }

  // Use API data if available, fall back to localStorage
  const sessionData = fullSessionData || localSessionData;

  if (!sessionData) {
    console.warn('[Generator] Reopen requested but no session data found');
    return false;
  }

  console.log('[Generator] Session brief:', sessionData.brief);
  console.log('[Generator] Session triggers:', sessionData.triggers);
  console.log('[Generator] Session ideas count:', sessionData.ideas?.length || 0);

  // Extract brief data (handle both object and string formats)
  // FIX: Include productId for full session rehydration
  let briefData = { product: '', benefit: '', formats: [], productId: null };
  if (typeof sessionData.brief === 'object' && sessionData.brief) {
    briefData = {
      product: sessionData.brief.product || '',
      benefit: sessionData.brief.benefit || '',
      formats: sessionData.brief.formats || sessionData.formats || [],
      productId: sessionData.brief.productId || null  // Restore productId from session
    };
  } else if (typeof sessionData.brief === 'string') {
    // Legacy format: brief was just a string (product name)
    briefData.product = sessionData.brief;
    briefData.formats = sessionData.formats || [];
  }

  // Restore brief state (use Store actions)
  GeneratorState.brief = briefData;
  // Also set formats via action (brief getter reads from generator.formats)
  setFormats(briefData.formats || []);
  console.log('[Generator] Restored brief:', GeneratorState.brief);

  // Restore selected triggers (use Store action)
  const triggers = (sessionData.triggers || []).map(t =>
    typeof t === 'string' ? t : t.code
  );
  setTriggers(triggers);
  console.log('[Generator] Restored triggers:', GeneratorState.selectedTriggers);

  // Convert flat ideas array to grouped format that Phase 4 expects
  // IMPORTANT: This also assigns stable IDs to ideas that don't have them
  const ideas = sessionData.ideas || [];
  GeneratorState.generatedIdeas = convertIdeasToGroupFormat(ideas);
  console.log('[Generator] Converted ideas to groups:', GeneratorState.generatedIdeas.length, 'groups');

  // HYDRATION FIX: Pre-populate preloadedImages map from the converted ideas
  // This ensures Phase 4 renders the real images immediately
  if (!GeneratorState.preloadedImages) {
    GeneratorState.preloadedImages = {};
  }
  for (const group of GeneratorState.generatedIdeas) {
    for (const idea of group.ideas) {
      // Check for camelCase OR snake_case
      const img = idea.imageUrl || idea.image_url;
      if (img) {
        GeneratorState.preloadedImages[idea.id] = img;
        console.log('[Generator] Restored image for idea:', idea.id);
      }
    }
  }

  // CRITICAL: Restore full session data for export functionality
  // This is what export.js needs to generate the full session export
  // See docs/_TASKS/80-download-session-reopen-bug.md
  if (fullSessionData) {
    const reconstructedSession = reconstructSessionForExport(fullSessionData);
    setSession(reconstructedSession);
    console.log('[Generator] Restored session for export, version:', reconstructedSession.version);
  } else {
    console.warn('[Generator] No full session data available - export will be limited');
  }

  // V5: Restore session brand (not current loaded brand)
  // This ensures reopened sessions show the brand that was used during generation
  // Check both camelCase and snake_case field names
  const sessionBrandId = sessionData.brandId || sessionData.brand_id;
  console.log('[Generator] Session brandId:', sessionBrandId);

  if (sessionBrandId) {
    try {
      const brandsResult = await api.getBrands();
      const brands = brandsResult.brands || brandsResult || [];
      const sessionBrand = brands.find(b => b.id === sessionBrandId);
      if (sessionBrand) {
        GeneratorState.sessionBrand = sessionBrand;
        // Also set currentBrand so the nav selector shows it
        setBrand(sessionBrand);
        console.log('[Generator] Restored session brand:', sessionBrand.name);

        // FIX: Restore product context if productId exists in brief
        if (briefData.productId) {
          try {
            const productsResult = await api.getProducts(sessionBrand.id);
            const products = productsResult.products || [];
            const sessionProduct = products.find(p => p.id === briefData.productId);
            if (sessionProduct) {
              GeneratorState.sessionProduct = sessionProduct;
              // Also set productData in brief for context bar and prompts
              GeneratorState.brief.productData = sessionProduct;
              GeneratorState.brief.productId = briefData.productId;
              // Set selectedProduct for image generation (uses selectedProduct || sessionProduct)
              GeneratorState.selectedProduct = sessionProduct;
              console.log('[Generator] Restored session product:', sessionProduct.name);
              console.log('[Generator] Restored session product description:', sessionProduct.description?.substring(0, 50) || 'none');
            } else {
              console.log('[Generator] Session product not found in brand products');
            }
          } catch (prodErr) {
            console.warn('[Generator] Could not fetch session product:', prodErr.message);
          }
        }
      } else {
        console.log('[Generator] Session brand not found in user brands');
      }
    } catch (e) {
      console.warn('[Generator] Could not fetch session brand:', e.message);
    }
  }

  // Clear the reopen data from localStorage
  localStorage.removeItem('cd_reopen_session');

  return true;
}

/**
 * Reconstruct session object from database format for export
 * Maps the DB session (with meta column containing phase data) to the
 * structure that export.js expects
 *
 * @param {Object} dbSession - Session from database API
 * @returns {Object} - Session object compatible with export formatters
 */
function reconstructSessionForExport(dbSession) {
  const meta = dbSession.meta || {};

  // ALWAYS rebuild results from database ideas to ensure we have correct UUIDs
  // The meta.results has original frontend IDs which don't match preloadedImages
  // Database ideas have the actual UUIDs that images are stored under
  let results = null;
  if (dbSession.ideas && dbSession.ideas.length > 0) {
    console.log('[Generator] Rebuilding results from DB ideas. Sample IDs:',
      dbSession.ideas.slice(0, 3).map(i => i.id));
    console.log('[Generator] Sample idea structure:', JSON.stringify(dbSession.ideas[0], null, 2).substring(0, 500));
    results = convertIdeasToResultsFormat(dbSession.ideas);
    console.log('[Generator] Rebuilt results. First group IDs:',
      results[0]?.ideas?.slice(0, 3).map(i => i.id));
  } else if (meta.results) {
    // Fallback to meta.results only if no database ideas available
    console.log('[Generator] Using meta.results (no DB ideas)');
    results = meta.results;
  }

  return {
    version: meta.version || dbSession.prompt_version || 'v5',
    timestamp: dbSession.created_at || new Date().toISOString(),
    brief: typeof dbSession.brief === 'object' ? dbSession.brief : {
      product: dbSession.brief || '',
      benefit: '',
      formats: dbSession.formats || []
    },
    triggers: (dbSession.triggers || []).map(t => {
      if (typeof t === 'string') {
        // Look up trigger details from DataStore
        const triggerData = window.DataStore?.getTriggerByCode(t);
        return {
          code: t,
          name: triggerData?.name || 'Unknown',
          question: triggerData?.question || ''
        };
      }
      return t;
    }),
    ideasCount: dbSession.ideas_count || dbSession.ideas?.length || 0,

    // Phase data from meta (this is what export needs)
    // DEBUG: Log phase1 structure
    ...(console.log('[Generator] Phase1 data for export:', {
      hasPhase1: !!meta.phase1,
      hasParsedResponse: !!meta.phase1?.parsedResponse,
      hasFragments: !!meta.phase1?.parsedResponse?.fragments,
      fragmentsLength: meta.phase1?.parsedResponse?.fragments?.length || 0
    }) || {}),
    phase1: meta.phase1 || null,
    phase2a: meta.phase2a || null,
    phase2b: meta.phase2b || null,
    phase3: meta.phase3 || null,

    // Results for final ideas section
    results: results,

    // Stats
    stats: {
      fragmentsGenerated: meta.fragmentsGenerated || meta.phase1?.fragmentsGenerated || 0,
      fragmentsInterpreted: meta.fragmentsInterpreted || meta.phase2a?.interpretedCount || 0,
      fragmentsSelected: meta.fragmentsSelected || meta.phase2b?.selectedCount || 0,
      formatsGenerated: meta.formatsGenerated || dbSession.formats?.length || 0,
      ideasGenerated: meta.ideas_generated || dbSession.ideas?.length || 0,
      totalDuration_ms: meta.totalDuration_ms || meta.duration_ms || 0,
      totalTokens: meta.totalTokens || 0,
      phases: 4
    }
  };
}

/**
 * Convert flat ideas array to results format for export
 * Groups ideas by trigger code in the format export expects
 *
 * @param {Array} ideas - Flat array of ideas from database
 * @returns {Array} - Grouped results array for export
 */
function convertIdeasToResultsFormat(ideas) {
  const grouped = {};

  ideas.forEach(idea => {
    const triggerCode = idea.trigger_code || idea.triggerCode || idea.trigger || 'A1';

    if (!grouped[triggerCode]) {
      const triggerData = window.DataStore?.getTriggerByCode(triggerCode);
      grouped[triggerCode] = {
        triggerCode: triggerCode,
        triggerName: triggerData?.name || 'Unknown',
        ideas: []
      };
    }

    grouped[triggerCode].ideas.push({
      // CRITICAL: Include id for image lookup in export
      id: idea.id,
      title: idea.meta?.title || idea.headline || 'Untitled',
      headline: idea.headline || '',
      subline: idea.meta?.subline || idea.subline || '',
      idea: idea.meta?.idea || idea.idea || '',
      visual: idea.visual || '',
      text: idea.visual || idea.text || '',
      format: idea.format || 'print',
      whyItWorks: idea.explanation || idea.whyItWorks || '',
      sourceFragment: idea.source_fragment || idea.sourceFragment || '',
      sourceTruth: idea.source_truth || idea.sourceTruth || '',
      // Include image URLs if present (for PDF export)
      image_url: idea.image_url || idea.imageUrl || null,
      imageUrl: idea.imageUrl || idea.image_url || null
    });
  });

  return Object.values(grouped);
}

/**
 * Convert flat ideas array to grouped format for Phase 4
 * Phase 4 expects: [{ trigger: {...}, ideas: [...] }, ...]
 * @param {Array} ideas - Flat array of ideas from session
 * @returns {Array} Grouped ideas array
 */
function convertIdeasToGroupFormat(ideas) {
  const DataStore = window.DataStore;
  const grouped = {};

  ideas.forEach(idea => {
    const triggerCode = idea.trigger || idea.triggerCode || idea.trigger_code || 'A1';

    if (!grouped[triggerCode]) {
      // Look up trigger details from DataStore (method is getTriggerByCode)
      const triggerData = DataStore.getTriggerByCode(triggerCode) || {
        code: triggerCode,
        name: 'Unknown Trigger',
        category: triggerCode.charAt(0),
        question: ''
      };

      grouped[triggerCode] = {
        trigger: {
          code: triggerCode,
          name: triggerData.name || 'Unknown',
          category: triggerData.category || triggerCode.charAt(0),
          question: triggerData.question || ''
        },
        ideas: []
      };
    }

    // Extract meta fields if present (DB stores complex data in meta JSONB)
    const meta = idea.meta || {};

    // Normalize idea to expected format
    // See docs/_TASKS/72-harvest-reopen.md for field mapping details
    grouped[triggerCode].ideas.push({
      id: idea.id || `idea_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: meta.title || idea.headline || idea.title || 'Untitled',
      headline: idea.headline || '',
      subline: meta.subline || idea.subline || null,
      text: idea.visual || idea.text || '',
      visual: idea.visual || '',
      format: idea.format || 'print',
      whyItWorks: idea.whyItWorks || idea.explanation || '',
      imageUrl: idea.imageUrl || idea.image_url || null,
      saved: idea.saved || idea.is_saved || false,
      isRefined: idea.refined || idea.isRefined || idea.is_refined || false,
      refinementCount: meta.refinementCount || idea.refinementCount || 0,
      critique: meta.critique || idea.critique || null,
      sourceFragment: idea.sourceFragment || idea.source_fragment || null,
      sourceTruth: idea.sourceTruth || idea.source_truth || null,
      // CRITICAL: Include alternativeHeadlines for headline navigation
      alternativeHeadlines: meta.alternativeHeadlines || idea.alternative_headlines || idea.alternativeHeadlines || [],
      currentHeadlineIndex: 0,
      // CRITICAL: These fields from meta are needed for proper card rendering (issue #72)
      idea: meta.idea || idea.idea || '',  // "THE IDEA" one-sentence concept
      headlineLabel: meta.headlineLabel || idea.headlineLabel || 'HEADLINE',  // Format-specific label
      sectionLabel: meta.sectionLabel || idea.sectionLabel || 'VISUAL'  // Format-specific label
    });
  });

  return Object.values(grouped);
}

// ============================================================================
// PHASE LOADER (hides content during initialization)
// ============================================================================

/**
 * Hide the phase transition loader
 * Called after navigation is complete to reveal the correct phase
 */
function hideLoader() {
  const loader = document.getElementById('generator-loader');
  if (loader) {
    loader.classList.add('generator-loader--hidden');
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// ============================================================================
// BRAND LOADING (V5)
// ============================================================================

// Shell color map for brand indicator
const SHELL_COLORS = {
  slate: '#64748b',
  orange: '#f97316',
  charcoal: '#374151',
  violet: '#8b5cf6',
  teal: '#14b8a6',
  blue: '#3b82f6',
  rose: '#f43f5e',
  amber: '#f59e0b',
  stone: '#78716c'
};

/**
 * Check if a brand is loaded from Dashboard and activate it
 * Reads from localStorage (set by Dashboard "LOAD" button)
 */
async function checkLoadedBrand() {
  // Check localStorage for loaded brand (set by Dashboard Load button)
  // Dashboard Storage helper JSON.stringifies values, so we need to parse
  const rawValue = localStorage.getItem('cd_loaded_brand');
  const loadedBrandId = rawValue ? JSON.parse(rawValue) : null;

  if (!loadedBrandId) {
    console.log('[Generator] No brand loaded - normal mode');
    return;
  }

  console.log('[Generator] Found loaded brand:', loadedBrandId);

  try {
    // Fetch brand details via API
    const response = await api.getBrands();
    const brands = response.brands || [];
    const brand = brands.find(b => b.id === loadedBrandId);

    if (brand) {
      setBrand(brand);
      // Cache brands list for selector
      cachedBrands = brands;
      // Pre-fill product input
      const productInput = document.getElementById('input-product');
      if (productInput && !productInput.value.trim()) {
        productInput.value = brand.name;
      }
      console.log('[Generator] Brand activated:', brand.name);
    } else {
      // Brand not found - clear stale reference
      console.warn('[Generator] Loaded brand not found, clearing');
      localStorage.removeItem('cd_loaded_brand');
    }
  } catch (err) {
    console.error('[Generator] Failed to fetch brand:', err);
    // Don't block generator - continue without brand
  }
}

/**
 * Eject brand - return to normal mode
 * Called from brand indicator button (legacy) or nav eject
 */
function ejectBrand() {
  ejectBrandFromNav();
}

// Expose ejectBrand globally for onclick handler
window.ejectBrand = ejectBrand;

// ============================================================================
// BRAND SELECTOR (V5)
// Button-based brand selector with modal picker
// ============================================================================

// Cache for brands list (populated once)
let cachedBrands = null;
let brandPickerModal = null;

// 8-bit sound effects for brand actions (same as dashboard)
const BrandSounds = {
  ctx: null,

  getContext() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.ctx;
  },

  play(type) {
    try {
      const ctx = this.getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      switch(type) {
        case 'click':
          osc.frequency.setValueAtTime(800, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.05);
          gain.gain.setValueAtTime(0.08, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.05);
          break;

        case 'load':
          // Rising arpeggio - cartridge loading
          osc.type = 'square';
          osc.frequency.setValueAtTime(262, ctx.currentTime);
          osc.frequency.setValueAtTime(330, ctx.currentTime + 0.08);
          osc.frequency.setValueAtTime(392, ctx.currentTime + 0.16);
          osc.frequency.setValueAtTime(523, ctx.currentTime + 0.24);
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.35);
          break;

        case 'eject':
          // Falling arpeggio - cartridge ejecting
          osc.type = 'square';
          osc.frequency.setValueAtTime(523, ctx.currentTime);
          osc.frequency.setValueAtTime(392, ctx.currentTime + 0.08);
          osc.frequency.setValueAtTime(330, ctx.currentTime + 0.16);
          osc.frequency.setValueAtTime(262, ctx.currentTime + 0.24);
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.35);
          break;
      }
    } catch (e) {
      // Silently fail if audio not available
    }
  }
};

/**
 * Initialize brand selector buttons and fetch brands
 * Called during generator initialization
 */
async function initBrandSelector() {
  try {
    // Fetch brands if not cached
    if (!cachedBrands) {
      const response = await api.getBrands();
      cachedBrands = response.brands || [];
    }

    // Bind click handlers to all brand selector buttons
    document.querySelectorAll('.dev-nav-brand, .nav-brand-btn').forEach(btn => {
      btn.addEventListener('click', openBrandPicker);
    });

    // Update button display based on current brand
    updateBrandSelectorButtons();

  } catch (err) {
    console.error('[Generator] Failed to init brand selector:', err);
  }
}

/**
 * Open the brand picker modal
 * Now fetches and displays products under each brand
 */
async function openBrandPicker() {
  // Play click sound
  BrandSounds.play('click');

  // Create modal if it doesn't exist
  if (!brandPickerModal) {
    createBrandPickerModal();
  }

  // Populate the brand list
  const list = document.getElementById('brand-picker-list');
  if (list) {
    const currentBrandId = getBrandId();
    const currentProductId = GeneratorState.brief?.productId || null;

    // Show loading state
    list.innerHTML = `
      <div class="brand-picker-loading">Loading brands...</div>
    `;

    // Show modal immediately
    brandPickerModal.classList.add('active');

    // Fetch products for all brands in parallel
    const brandProductsMap = {};
    await Promise.all(cachedBrands.map(async (brand) => {
      try {
        const response = await api.getProducts(brand.id);
        if (response.success && response.products?.length > 0) {
          brandProductsMap[brand.id] = response.products;
        }
      } catch (err) {
        console.warn(`[Generator] Failed to load products for brand ${brand.name}:`, err);
      }
    }));

    // Render brands with products
    list.innerHTML = `
      <button class="brand-picker-item ${!currentBrandId ? 'active' : ''}" data-brand-id="" data-product-id="">
        <div class="brand-picker-item-header">
          <span class="brand-picker-led"></span>
          <span class="brand-picker-name">No brand</span>
          <span class="brand-picker-industry">Generate without brand context</span>
        </div>
      </button>
      ${cachedBrands.map(brand => {
        const shellColor = SHELL_COLORS[brand.shellColor || brand.shell_color] || '#64748b';
        const colors = brand.colors || [];
        const products = brandProductsMap[brand.id] || [];
        const isBrandActive = brand.id === currentBrandId && !currentProductId;
        const hasProducts = products.length > 0;

        return `
          <div class="brand-picker-group">
            <button class="brand-picker-item ${isBrandActive ? 'active' : ''}"
                    data-brand-id="${brand.id}"
                    data-product-id=""
                    style="--shell-color: ${shellColor}">
              <div class="brand-picker-item-header" style="background: ${shellColor}">
                <span class="brand-picker-led"></span>
                <span class="brand-picker-name">${escapeHtml(brand.name)}</span>
                <span class="brand-picker-industry">${escapeHtml(brand.industry || brand.category || '')}</span>
              </div>
              <div class="brand-picker-item-footer">
                <div class="brand-picker-colors">
                  ${colors.slice(0, 3).map(c => `<span class="brand-picker-swatch" style="background: ${c}"></span>`).join('')}
                </div>
                ${hasProducts ? `<span class="brand-picker-product-count">${products.length} product${products.length > 1 ? 's' : ''}</span>` : ''}
              </div>
            </button>
            ${hasProducts ? `
              <div class="brand-picker-products">
                <div class="brand-picker-products-header">SELECT PRODUCT</div>
                <button class="brand-picker-product brand-picker-product-none ${brand.id === currentBrandId && !currentProductId ? 'active' : ''}"
                        data-brand-id="${brand.id}"
                        data-product-id=""
                        data-product-name=""
                        data-product-benefit=""
                        data-product-description="">
                  <span class="product-icon"></span>
                  <span class="product-name">No product</span>
                  <span class="product-desc">Use brand only, type custom product name</span>
                </button>
                ${products.map(product => {
                  const isProductActive = brand.id === currentBrandId && product.id === currentProductId;
                  return `
                    <button class="brand-picker-product ${isProductActive ? 'active' : ''}"
                            data-brand-id="${brand.id}"
                            data-product-id="${product.id}"
                            data-product-name="${escapeAttr(product.name)}"
                            data-product-benefit="${escapeAttr(product.default_benefit || '')}"
                            data-product-description="${escapeAttr(product.description || '')}">
                      <div class="product-header">
                        <span class="product-icon"></span>
                        <span class="product-name">${escapeHtml(product.name)}</span>
                      </div>
                      ${product.description ? `<span class="product-desc">${escapeHtml(product.description)}</span>` : ''}
                    </button>
                  `;
                }).join('')}
              </div>
            ` : ''}
          </div>
        `;
      }).join('')}
      <a href="/features/dashboard/pages/brands.html" class="brand-picker-footer-link">View all brands →</a>
    `;

    // Bind click handlers for brands (clicking the brand header, not product)
    list.querySelectorAll('.brand-picker-item').forEach(item => {
      item.addEventListener('click', () => {
        console.log('[Generator] Brand item clicked (no product):', item.dataset.brandId);
        selectBrandFromPicker(item.dataset.brandId, null);
      });
    });

    // Bind click handlers for products (with stopPropagation to prevent brand click)
    list.querySelectorAll('.brand-picker-product').forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();  // Prevent bubbling to parent brand item
        console.log('[Generator] Product button clicked:', {
          brandId: item.dataset.brandId,
          productId: item.dataset.productId || '(none)',
          productName: item.dataset.productName || '(none)'
        });
        selectBrandFromPicker(
          item.dataset.brandId,
          item.dataset.productId,
          item.dataset.productName,
          item.dataset.productBenefit,
          item.dataset.productDescription
        );
      });
    });
  }
}

/**
 * Escape HTML for safe rendering
 */
function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Escape attribute value
 */
function escapeAttr(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

/**
 * Close the brand picker modal
 */
function closeBrandPicker() {
  if (brandPickerModal) {
    brandPickerModal.classList.remove('active');
  }
}

/**
 * Create the brand picker modal DOM
 */
function createBrandPickerModal() {
  brandPickerModal = document.createElement('div');
  brandPickerModal.id = 'brand-picker-modal';
  brandPickerModal.className = 'brand-picker-modal';
  brandPickerModal.innerHTML = `
    <div class="brand-picker-overlay" onclick="closeBrandPicker()"></div>
    <div class="brand-picker-content">
      <div class="brand-picker-titlebar">
        <button class="brand-picker-close" onclick="closeBrandPicker()"></button>
        <span class="brand-picker-title">Select Brand</span>
      </div>
      <div class="brand-picker-search">
        <input type="text" class="brand-picker-search-input" id="brand-picker-search" placeholder="Search brands...">
      </div>
      <div class="brand-picker-list" id="brand-picker-list">
        <!-- Populated dynamically -->
      </div>
    </div>
  `;

  // Add search functionality - only filter brand groups, always show "No brand"
  const searchInput = brandPickerModal.querySelector('#brand-picker-search');
  searchInput.addEventListener('input', (e) => {
    const query = e.target.value.toLowerCase();
    const brandGroups = brandPickerModal.querySelectorAll('.brand-picker-group');
    brandGroups.forEach(item => {
      const name = item.querySelector('.brand-picker-name')?.textContent.toLowerCase() || '';
      const industry = item.querySelector('.brand-picker-industry')?.textContent.toLowerCase() || '';
      if (name.includes(query) || industry.includes(query)) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });
  });
  document.body.appendChild(brandPickerModal);

  // Expose close function globally
  window.closeBrandPicker = closeBrandPicker;
}

/**
 * Handle brand/product selection from the picker modal
 * @param {string} brandId - Selected brand ID or empty string
 * @param {string|null} productId - Selected product ID (optional)
 * @param {string|null} productName - Product name for pre-filling
 * @param {string|null} productBenefit - Product default benefit
 * @param {string|null} productDescription - Product description
 */
function selectBrandFromPicker(brandId, productId = null, productName = null, productBenefit = null, productDescription = null) {
  const previousBrand = GeneratorState.currentBrand;
  // CRITICAL: Capture previousProduct BEFORE setBrand() clears it
  const previousProduct = GeneratorState.selectedProduct;
  const productInput = document.getElementById('input-product');
  const benefitInput = document.getElementById('input-benefit');
  const currentProductValue = productInput?.value?.trim() || '';

  if (!brandId) {
    // "No brand" selected - clear brand and product
    clearBrand();
    updateBrief({ productId: null, productData: null });
    localStorage.removeItem('cd_loaded_brand');

    // Clear product input if it matches the previous brand name OR previous product name
    const shouldClearProduct = !currentProductValue ||
                               (previousBrand && currentProductValue === previousBrand.name) ||
                               (previousProduct && currentProductValue === previousProduct.name);
    if (shouldClearProduct && productInput) {
      productInput.value = '';
      updateBrief({ product: '' });
      productInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Play eject sound if we had a brand before
    if (previousBrand) {
      BrandSounds.play('eject');
    }

    // Remove Phase 1 product picker since no brand is selected
    if (typeof window.refreshProductsForBrand === 'function') {
      window.refreshProductsForBrand(null);
    }

    console.log('[Generator] Brand cleared');
  } else {
    // Find the brand in cache
    const brand = cachedBrands?.find(b => b.id === brandId);
    if (!brand) {
      console.warn('[Generator] Selected brand not found in cache');
      closeBrandPicker();
      return;
    }

    // Set the brand
    setBrand(brand);
    localStorage.setItem('cd_loaded_brand', JSON.stringify(brandId));

    // Handle product selection
    if (productId && productName) {
      // Product selected - use product data
      // CRITICAL: Set selectedProduct for image generation prompts
      const productData = {
        id: productId,
        name: productName,
        description: productDescription || null,
        default_benefit: productBenefit || null
      };
      GeneratorState.selectedProduct = productData;

      // Update Store with product data
      updateBrief({ productId, product: productName, productData });

      if (productInput) {
        productInput.value = productName;
        productInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Pre-fill benefit if available
      if (productBenefit && benefitInput) {
        benefitInput.value = productBenefit;
        updateBrief({ benefit: productBenefit });
        benefitInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Update Phase 1 product picker dropdown to match
      const productPickerSelect = document.getElementById('product-picker-select');
      if (productPickerSelect) {
        productPickerSelect.value = productId;
      }
      // Show Phase 1 product info panel
      const productInfoEl = document.getElementById('product-info');
      const productDescEl = document.getElementById('product-info-description');
      if (productInfoEl && productDescEl && productDescription) {
        productDescEl.innerHTML = `<strong>Description:</strong> ${escapeHtml(productDescription)}${productBenefit ? `<br><strong>Benefit:</strong> ${escapeHtml(productBenefit)}` : ''}`;
        productInfoEl.style.display = 'block';
      }

      console.log(`[Generator] Brand + Product selected: ${brand.name} → ${productName}`);
      console.log(`[Generator] selectedProduct set:`, productData.name);
      if (productDescription) {
        console.log(`[Generator] Product description: ${productDescription.substring(0, 50)}...`);
      }
    } else {
      // Just brand selected (no product)
      // Clear product data (previousProduct was captured at function start before setBrand cleared it)
      GeneratorState.selectedProduct = null;

      // Clear product data in Store
      updateBrief({ productId: null, productData: null });

      // Update product input:
      // - If empty, set to brand name
      // - If it matches previous brand name, update to new brand name
      // - If it matches previously selected product name, update to brand name
      // - If user typed something custom, leave it alone
      if (productInput) {
        const shouldUpdate = !currentProductValue ||
                             (previousBrand && currentProductValue === previousBrand.name) ||
                             (previousProduct && currentProductValue === previousProduct.name);
        if (shouldUpdate) {
          productInput.value = brand.name;
          updateBrief({ product: brand.name });
          productInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      // Also clear benefit if it came from the product
      if (previousProduct && benefitInput && benefitInput.value === previousProduct.default_benefit) {
        benefitInput.value = '';
        updateBrief({ benefit: '' });
        benefitInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Update Phase 1 product picker dropdown to match
      const productPickerSelect = document.getElementById('product-picker-select');
      if (productPickerSelect) {
        productPickerSelect.value = '';  // Reset to "no product" option
      }
      // Hide Phase 1 product info panel
      const productInfoEl = document.getElementById('product-info');
      if (productInfoEl) {
        productInfoEl.style.display = 'none';
      }

      console.log('[Generator] Brand selected (no product):', brand.name);
    }

    // Play load sound
    BrandSounds.play('load');

    // Refresh Phase 1 product picker for the new brand
    if (typeof window.refreshProductsForBrand === 'function') {
      window.refreshProductsForBrand(brandId, productId || null);
    }
  }

  // Update all brand selector rows (the expanded card)
  updateBrandSelectorButtons();

  // Close the modal
  closeBrandPicker();
}

/**
 * Update all brand selector rows to reflect current state
 * When brand is loaded, shows expanded card with details + product + EJECT
 * When no brand, shows simple "Select Brand" button
 */
function updateBrandSelectorButtons() {
  const brand = GeneratorState.currentBrand;
  const productId = GeneratorState.brief?.productId;
  const productName = GeneratorState.brief?.product;

  document.querySelectorAll('.dev-nav-brand, .nav-brand-btn').forEach(container => {
    if (brand) {
      // Show expanded brand card
      const shellColor = SHELL_COLORS[brand.shellColor || brand.shell_color] || '#64748b';
      const colors = brand.colors || [];

      // Check if a product is selected (productId set AND product name differs from brand name)
      const hasProduct = productId && productName && productName !== brand.name;

      container.classList.add('has-brand');
      container.innerHTML = `
        <div class="brand-card-mini" style="--shell-color: ${shellColor}">
          <div class="brand-card-header">
            <span class="brand-card-led"></span>
            <span class="brand-card-name">${escapeHtml(brand.name)}</span>
            <span class="brand-card-industry">${escapeHtml(brand.industry || '')}</span>
          </div>
          ${hasProduct ? `
            <div class="brand-card-product">
              <span class="brand-card-product-label">PRODUCT:</span>
              <span class="brand-card-product-name">${escapeHtml(productName)}</span>
            </div>
          ` : ''}
          <div class="brand-card-footer">
            <div class="brand-card-colors">
              ${colors.slice(0, 4).map(c => `<span class="brand-card-swatch" style="background: ${c}"></span>`).join('')}
            </div>
            <button class="brand-card-eject" onclick="event.stopPropagation(); window.ejectBrandFromNav()">EJECT</button>
          </div>
        </div>
      `;
    } else {
      // Show simple select button
      container.classList.remove('has-brand');
      container.innerHTML = `
        <span class="dev-nav-brand-icon">●</span>
        <span class="dev-nav-brand-label">BRAND</span>
        <span class="dev-nav-brand-value">Select brand...</span>
      `;
    }
  });
}

/**
 * Eject brand from nav button (wrapper to prevent modal opening)
 */
function ejectBrandFromNav() {
  const currentBrand = GeneratorState.currentBrand;
  console.log('[Generator] ejectBrandFromNav called, current brand:', currentBrand?.name || 'NONE', currentBrand?.id || 'NO ID');

  const productInput = document.getElementById('input-product');
  const currentProductValue = productInput?.value?.trim() || '';

  // Clear brand state
  clearBrand();
  localStorage.removeItem('cd_loaded_brand');

  // Clear product ID from brief (important for generation)
  updateBrief({ productId: null, productData: null });

  console.log('[Generator] After clearBrand, GeneratorState.currentBrand:', GeneratorState.currentBrand);

  // Clear product field if it matches brand name (user didn't customize it)
  if (currentBrand && currentProductValue === currentBrand.name) {
    productInput.value = '';
    updateBrief({ product: '' });
    productInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Update UI
  updateBrandSelectorButtons();

  // Play eject sound
  BrandSounds.play('eject');

  console.log('[Generator] Brand ejected from nav. getBrandId() now returns:', getBrandId());
  Toast.show('Brand ejected', 'info');
}

// Expose globally
window.ejectBrandFromNav = ejectBrandFromNav;
window.SHELL_COLORS = SHELL_COLORS;

// Legacy alias for compatibility
const populateBrandSelector = initBrandSelector;
const updateBrandSelector = updateBrandSelectorButtons;

/**
 * Check for Quick Launch mode (from Dashboard)
 * Reads preload state from sessionStorage and hydrates GeneratorState
 * @returns {boolean} True if quick launch mode is active
 */
async function checkQuickLaunchMode() {
  const urlParams = new URLSearchParams(window.location.search);
  const isAutoload = urlParams.get('autoload') === 'true';

  if (!isAutoload) return false;

  const preloadDataRaw = sessionStorage.getItem('cd_generator_preload');
  if (!preloadDataRaw) {
    console.warn('[Generator] Quick Launch: autoload flag set but no preload data found');
    return false;
  }

  try {
    const preloadData = JSON.parse(preloadDataRaw);
    console.log('[Generator] Quick Launch mode detected:', preloadData);

    // IMPORTANT: Load brand FIRST (setBrand clears product data)
    if (preloadData.brandId) {
      try {
        const brandsResult = await api.getBrands();
        const brands = brandsResult.brands || [];
        const brand = brands.find(b => b.id === preloadData.brandId);
        if (brand) {
          setBrand(brand);
          cachedBrands = brands;
          console.log('[Generator] Quick Launch: Brand loaded:', brand.name);
        }
      } catch (e) {
        console.warn('[Generator] Quick Launch: Failed to load brand:', e.message);
      }
    }

    // THEN hydrate GeneratorState with preload data (after setBrand)
    GeneratorState.brief = {
      product: preloadData.brief.product || '',
      benefit: preloadData.brief.benefit || '',
      formats: preloadData.brief.formats || ['print'],
      productId: preloadData.brief.productId || null,
      productData: preloadData.brief.productData || null,
      // Store custom format description for backend
      customFormatDescription: preloadData.brief.customFormatDescription || null
    };

    // Set selectedProduct for image generation (CRITICAL for correct prompts)
    if (preloadData.brief.productData) {
      GeneratorState.selectedProduct = preloadData.brief.productData;
      console.log('[Generator] Quick Launch: selectedProduct set:', preloadData.brief.productData.name);
    }

    setTriggers(preloadData.triggers || []);
    GeneratorState.ideasCount = preloadData.ideasCount || 3;

    // Store return URL for Phase 4 navigation
    GeneratorState.quickLaunchReturnUrl = preloadData.returnUrl || null;

    // Update UI elements to reflect state
    const productInput = document.getElementById('input-product');
    const benefitInput = document.getElementById('input-benefit');

    if (productInput) {
      productInput.value = GeneratorState.brief.product;
    }
    if (benefitInput) {
      benefitInput.value = GeneratorState.brief.benefit;
    }

    // Update format checkboxes
    document.querySelectorAll('.format-card').forEach(card => {
      const format = card.dataset.format;
      const isSelected = GeneratorState.brief.formats.includes(format);
      card.classList.toggle('selected', isSelected);
      const checkbox = card.querySelector('input[type="checkbox"]');
      if (checkbox) checkbox.checked = isSelected;
    });

    // Clean up sessionStorage
    sessionStorage.removeItem('cd_generator_preload');

    // Clean URL (remove autoload param without reload)
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    console.log('[Generator] Quick Launch: State hydrated, jumping to Phase 3');
    return true;

  } catch (e) {
    console.error('[Generator] Quick Launch: Failed to parse preload data:', e);
    sessionStorage.removeItem('cd_generator_preload');
    return false;
  }
}

/**
 * Initialize the generator (called after auth is confirmed)
 */
async function initializeGenerator() {
  // Access DataStore from global scope (defined in main.js)
  const DataStore = window.DataStore;

  // 0. Initialize Toast notifications (for error feedback)
  Toast.init();

  // 0b. Enable haptic feedback for mobile devices
  Haptics.autoAttach();

  // 1. Load data FIRST
  await DataStore.loadCategories();
  await DataStore.loadTriggers();

  // 2. Cache DOM elements SECOND
  const els = cacheElements();
  setElements(els);

  // 3. Register phase initializers (CRITICAL - must happen before any navigation)
  // Phase 1 is called directly, not registered (it runs immediately)
  registerPhaseInit(2, initPhase2);
  registerPhaseInit(3, runDetonation);
  registerPhaseInit(4, initPhase4);

  // 4. Initialize development tools
  initClickablePhases();

  // 5. Initialize video generation
  initVideoGeneration();

  // 5b. Initialize version history
  initVersionHistory();

  // 5c. Check for Quick Launch mode (from Dashboard Quick Launch modal)
  const isQuickLaunch = await checkQuickLaunchMode();
  if (isQuickLaunch) {
    // Populate brand selector
    await populateBrandSelector();
    // Jump directly to Phase 3 (Detonation)
    console.log('[Generator] Quick Launch: Skipping to Phase 3');
    goToPhase(3);
    hideLoader();
    return;
  }

  // 5d. Check for session reopen URL param FIRST
  // If ?reopen= is present, clear stale sessionStorage and use fresh API data
  const urlParams = new URLSearchParams(window.location.search);
  const hasReopenParam = urlParams.has('reopen');

  if (hasReopenParam) {
    // Clear sessionStorage to ensure we get fresh data from API
    clearSessionState();
    console.log('[Generator] Reopen detected - cleared sessionStorage for fresh API data');
  }

  // 5e. Try to restore session state from sessionStorage (page refresh recovery)
  // This runs AFTER reopen check so reopen always gets fresh API data
  const wasRestored = !hasReopenParam && restoreSessionState();
  if (wasRestored && GeneratorState.phase === 4 && GeneratorState.generatedIdeas.length > 0) {
    console.log('[Generator] Session restored from sessionStorage after page refresh');
    await populateBrandSelector();
    goToPhase(4);
    hideLoader();
    return;
  }

  // 6. Check for URL parameters
  checkPreselectedTrigger();

  // 7. Check for session reopen from Dashboard (async - fetches full data for export)
  const isReopening = await checkReopenSession();

  // 8. Check for loaded brand from Dashboard (V5 feature)
  // Skip if reopening a session - session brand takes priority
  if (!isReopening) {
    await checkLoadedBrand();
  }

  // 8b. Populate brand selector dropdown (V5 feature)
  await populateBrandSelector();

  // 9. Check backend status (async, doesn't block)
  checkBackendStatus();

  // 10. Start with Phase 1 OR jump to Phase 4 if reopening a session
  if (isReopening && GeneratorState.generatedIdeas.length > 0) {
    console.log('[Generator] Jumping to Phase 4 with reopened session');
    goToPhase(4);
    hideLoader();
  } else {
    initPhase1();
    hideLoader();
  }
}

/**
 * Main initialization function
 * Runs when DOM is fully loaded
 *
 * Uses unified auth-init.js for clean auth handling
 */
document.addEventListener('DOMContentLoaded', async () => {
  // Use unified auth initialization (handles auth check, login form, and callbacks)
  await initGeneratorAuth(async (user, profile) => {
    // Sync user to generator state
    setUser(user, profile);

    // Listen for auth state changes
    authState.onStateChange((u, p) => {
      setUser(u, p);
    });

    console.log('[Generator] Authenticated as:', authState.displayName, 'Credits:', authState.credits);

    // Initialize the generator
    await initializeGenerator();
  });
});

// ============================================================================
// GLOBAL EXPORTS
// ============================================================================

// Export goToPhase for any legacy code that might need it
window.goToPhase = goToPhase;
